import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../ui/Modal';
import ShopBannerStatus from '../shopBanners/ShopBannerStatus';
import KlaviyoLink from '../placements/KlaviyoLink';
import InstagramLink from '../placements/InstagramLink';
import { Field } from '../ui/Field';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useRates } from '../../lib/api/rateCard';
import { HeatmapDatePicker } from '../ui/HeatmapDatePicker';
import ConfirmDialog from '../ui/ConfirmDialog';
import BrandRemovalCheck, { RemovalItem } from './BrandRemovalCheck';
import { getCampaign, useCampaigns, useUpdateCampaign } from '../../lib/api/campaigns';
import { assetDisplayTitle } from '../../lib/assetTitle';
import { fmtShort } from '../../lib/dates';
import {
  useAssetMetrics,
  listAssets,
  useAssets,
  useCreateAsset,
  useDeleteAsset,
  useUpdateAsset,
} from '../../lib/api/assets';
import { planningConflicts, conflictLine } from '../../lib/homepageCapacity';
import { shortAssetType } from '../../lib/assetTitle';
import { METRIC_FIELDS_BY_CATEGORY, computeRates } from '../../lib/metrics';
import { useCreateTask, useTeam } from '../../lib/api/tasks';
import { slugify, useBrands } from '../../lib/api/brands';
import { useToast } from '../ui/Toast';
import { isAnimatedPicture, openDataUrl, readAsDataUrl } from '../../lib/dataUrl';
import { pastedPicture } from '../../lib/api/placementMap';
import AssigneePicker from '../tasks/AssigneePicker';
import { AssigneeStack } from '../../lib/brandAvatars';
import BrandChipPicker from '../ui/BrandChipPicker';
import { statusClass } from '../ui/Chip';
import CommentsSection from '../comments/CommentsSection';
import {
  ASSET_CATEGORIES,
  ASSET_STATUSES,
  ASSET_TYPES_BY_CATEGORY,
  AssetCategory,
  AssetStatus,
  AssetType,
  CampaignAsset,
} from '../../lib/types';

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Resize a screenshot client-side and re-encode as JPEG so it fits inside
// the JSON patch body without hitting Django's upload-size cap. Typical
// 5 MB phone screenshot ends up ~300-500 KB.
const MAX_ANIMATED_BYTES = 5 * 1024 * 1024;

const compressScreenshot = (file: File, maxDim = 1600, quality = 0.85): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * ratio));
        const h = Math.max(1, Math.round(img.height * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  });

// Forgive missing scheme on pasted URLs so DRF's URLField doesn't reject
// the whole patch (which would also drop the screenshot in the same call).
const normalizeUrl = (raw: string): string => {
  const u = raw.trim();
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  return `https://${u}`;
};

// Pick a human-readable error message from a DRF error response.
const firstErrorMessage = (data: unknown): string => {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    for (const v of Object.values(data as Record<string, unknown>)) {
      if (Array.isArray(v) && v.length > 0) return String(v[0]);
      if (typeof v === 'string') return v;
    }
  }
  return '';
};

interface Props {
  open: boolean;
  onClose: () => void;
  defaultCampaignId?: string;
  /** When opened from a brand context (e.g. BrandDetail) — preselects these
   *  brands so the asset is automatically tagged. */
  defaultBrandIds?: string[];
  defaultDate?: string;
  defaultCategory?: AssetCategory;
  defaultType?: AssetType;
  asset?: CampaignAsset | null;
  /** When set, the modal opens in *create* mode but pre-fills most fields
   *  from this source asset (everything except status, dates, and proof).
   *  Used by the "Duplicate" button to start from an existing asset. */
  duplicateOf?: CampaignAsset | null;
  onCreated?: (asset: CampaignAsset) => void;
}

export const CreateAssetModal = ({
  open,
  onClose,
  defaultCampaignId,
  defaultBrandIds,
  defaultDate,
  defaultCategory,
  defaultType,
  asset,
  duplicateOf,
  onCreated,
}: Props) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { data: campaigns = [] } = useCampaigns();
  const { data: brands = [] } = useBrands();
  const { data: team = [] } = useTeam();
  const createAsset = useCreateAsset();
  const updateAsset = useUpdateAsset();
  const updateCampaign = useUpdateCampaign();
  const deleteAsset = useDeleteAsset();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const onDelete = async () => {
    if (!asset) return;
    try {
      await deleteAsset.mutateAsync(asset.id);
      toast.push('Placement deleted');
      setConfirmDelete(false);
      onClose();
    } catch {
      toast.push('Could not delete placement');
    }
  };
  const createTask = useCreateTask();
  const editing = !!asset;

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<AssetCategory>(defaultCategory ?? 'Social Media');
  const [assetType, setAssetType] = useState<AssetType>(
    defaultType ?? ASSET_TYPES_BY_CATEGORY[defaultCategory ?? 'Social Media'][0],
  );
  const [campaign, setCampaign] = useState(defaultCampaignId ?? '');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [publishDate, setPublishDate] = useState(defaultDate ?? todayISO());
  const [endDate, setEndDate] = useState<string>('');
  const [publishTime, setPublishTime] = useState('');
  const [brandSpots, setBrandSpots] = useState('');
  // What the slots are sold as (a Placements & prices line); '' = automatic.
  const [soldAs, setSoldAs] = useState('');
  const { data: rates } = useRates();
  const [status, setStatus] = useState<AssetStatus>('Not started');
  const [description, setDescription] = useState('');
  const [caption, setCaption] = useState('');
  const [dedicatedBrand, setDedicatedBrand] = useState<string>('');
  const [proofUrl, setProofUrl] = useState('');
  const [metrics, setMetrics] = useState<Record<string, string>>({});
  const measured = useAssetMetrics(asset?.id);
  // All proof screenshots in order; the first is saved as proof_screenshot.
  const [proofShots, setProofShots] = useState<string[]>([]);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const takeScreenshot = async (file: File) => {
    setScreenshotLoading(true);
    try {
      // An animated GIF/WebP (a video story) keeps its animation; anything
      // else is shrunk to a JPEG.
      if (/^image\/(gif|webp)$/.test(file.type) && file.size > MAX_ANIMATED_BYTES) {
        toast.push('That animation is too big (max 5 MB) — make it shorter or smaller.');
        return;
      }
      const shot = /^image\/(gif|webp)$/.test(file.type)
        ? await readAsDataUrl(file)
        : await compressScreenshot(file);
      setProofShots((prev) => [...prev, shot]);
    } catch (err) {
      console.error(err);
      toast.push('Could not read that image — try another file.');
    } finally {
      setScreenshotLoading(false);
    }
  };
  const [createTaskAlso, setCreateTaskAlso] = useState(false);
  const [taskName, setTaskName] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskAssigneeIds, setTaskAssigneeIds] = useState<number[]>([]);
  const [taskPickerOpen, setTaskPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (asset) {
      setTitle(asset.title);
      setCategory(asset.category);
      setAssetType(asset.asset_type);
      setCampaign(asset.campaign ?? '');
      setSelectedBrands(asset.brands ?? []);
      setPublishDate(asset.publish_date ?? '');
      setEndDate(asset.end_date ?? '');
      setPublishTime(asset.publish_time ?? '');
      setBrandSpots(asset.brand_spots ? String(asset.brand_spots) : '');
      setSoldAs(asset.sold_as != null ? String(asset.sold_as) : '');
      setStatus(asset.status);
      setDescription(asset.description);
      setCaption(asset.caption);
      setDedicatedBrand(asset.dedicated_brand ?? '');
      setProofUrl(asset.proof_url ?? '');
      setMetrics(
        Object.fromEntries(
          Object.entries(asset.metrics ?? {}).map(([k, v]) => [k, v == null ? '' : String(v)]),
        ),
      );
      setProofShots([asset.proof_screenshot, ...(asset.proof_extra_screenshots ?? [])].filter(Boolean));
      setScreenshotLoading(false);
      setCreateTaskAlso(false);
      setTaskName('');
      setTaskDueDate('');
      setTaskAssigneeIds([]);
      setTaskPickerOpen(false);
    } else if (duplicateOf) {
      // Duplicate flow: carry everything except status, dates and proof.
      setTitle(duplicateOf.title);
      setCategory(duplicateOf.category);
      setAssetType(duplicateOf.asset_type);
      setCampaign(duplicateOf.campaign ?? defaultCampaignId ?? '');
      setSelectedBrands(duplicateOf.brands ?? []);
      setPublishDate(defaultDate ?? todayISO());
      setEndDate('');
      setPublishTime(duplicateOf.publish_time ?? '');
      setBrandSpots(duplicateOf.brand_spots ? String(duplicateOf.brand_spots) : '');
      setSoldAs(duplicateOf.sold_as != null ? String(duplicateOf.sold_as) : '');
      setStatus('Not started');
      setDescription(duplicateOf.description);
      setCaption(duplicateOf.caption);
      setDedicatedBrand(duplicateOf.dedicated_brand ?? '');
      setProofUrl('');
      setProofShots([]);
      setScreenshotLoading(false);
      setCreateTaskAlso(false);
      setTaskName('');
      setTaskDueDate('');
      setTaskAssigneeIds([]);
      setTaskPickerOpen(false);
    } else {
      const cat = defaultCategory ?? 'Social Media';
      const initialType = defaultType ?? ASSET_TYPES_BY_CATEGORY[cat][0];
      // The title is an optional label now — the display name is generated
      // from campaign + type, so new placements start with no label.
      setTitle('');
      setCategory(cat);
      setAssetType(initialType);
      // If we were launched from a campaign context, prefill that. Otherwise
      // start blank so the user can deliberately pick "campaign or brands".
      setCampaign(defaultCampaignId ?? '');
      setSelectedBrands(defaultBrandIds ?? []);
      setPublishDate(defaultDate ?? todayISO());
      setEndDate('');
      setPublishTime('');
      setBrandSpots('');
      setSoldAs('');
      setStatus('Not started');
      setDescription('');
      setCaption('');
      setDedicatedBrand('');
      setProofUrl('');
      setProofShots([]);
      setScreenshotLoading(false);
      setCreateTaskAlso(false);
      setTaskName('');
      setTaskDueDate('');
      setTaskAssigneeIds([]);
      setTaskPickerOpen(false);
    }
  }, [open, asset, duplicateOf, defaultCampaignId, defaultBrandIds, defaultDate, defaultCategory, defaultType, campaigns, brands]);

  const allowedTypes = useMemo(() => ASSET_TYPES_BY_CATEGORY[category], [category]);
  useEffect(() => {
    if (!allowedTypes.includes(assetType)) setAssetType(allowedTypes[0]);
  }, [allowedTypes, assetType]);

  const pending = createAsset.isPending || updateAsset.isPending || createTask.isPending;
  const hasContext = !!campaign || selectedBrands.length > 0;
  // publishDate is optional — empty = lands in the "To plan" tray.
  // Title is an optional label — only context (campaign or brands) is required.
  const canSubmit = hasContext && !pending && !screenshotLoading;

  // Homepage capacity check: warn (never block) when the chosen type +
  // dates collide with what's already planned on the homepage.
  const { data: homepageAssets = [] } = useAssets({ category: 'Homepage' });
  const capacityConflicts = useMemo(() => {
    if (category !== 'Homepage' || !publishDate) return [];
    return planningConflicts(assetType, publishDate, endDate || null, homepageAssets, asset?.id);
  }, [category, assetType, publishDate, endDate, homepageAssets, asset?.id]);

  const toggleBrand = (bid: string) =>
    setSelectedBrands((prev) =>
      prev.includes(bid) ? prev.filter((x) => x !== bid) : [...prev, bid],
    );

  // Empty or 0 = a normal placement (no brand spots).
  const spotsValue = Number(brandSpots) >= 1 ? Math.floor(Number(brandSpots)) : null;
  // Lines this type can be sold as: its own line(s) and packs that include it.
  const soldAsChoices = (rates ?? []).filter(
    (r) => r.active && (r.asset_type === assetType || (r.pack_types ?? []).includes(assetType)),
  );
  const soldAsValue = spotsValue && soldAs && soldAsChoices.some((r) => String(r.id) === soldAs) ? Number(soldAs) : null;

  // Brands taken off this placement: remind where they still are before saving.
  const [removalItems, setRemovalItems] = useState<RemovalItem[] | null>(null);
  const submit = async () => {
    if (!canSubmit) return;
    const removed = editing ? ((asset!.brands as string[]) ?? []).filter((b) => !selectedBrands.includes(b)) : [];
    if (!removed.length || !campaign) return doSubmit([]);
    try {
      const [others, camp] = await Promise.all([listAssets({ campaign }), getCampaign(campaign)]);
      const nameOf = (id: string) => brands.find((b) => b.id === id)?.name ?? id;
      setRemovalItems(removed.map((bid) => {
        const still = others.filter((a) => a.id !== asset!.id && ((a.brands as string[]) ?? []).includes(bid));
        const booked = (asset!.booking_order ?? []).some((o) => o.brand_id === bid && o.signed_up_at);
        const lines = [
          ...(booked ? ['Booked this slot on the booking page — it stays Signed up and this slot stays on its invoice.'] : []),
          still.length
            ? `Still on: ${still.slice(0, 6).map((a) => `${assetDisplayTitle(a)}${a.publish_date ? ` · ${fmtShort(a.publish_date)}` : ''}`).join(', ')}${still.length > 6 ? ` and ${still.length - 6} more` : ''}.`
            : 'Not on any other placement of this campaign.',
          `Stays in the campaign “${camp.name}”.`,
        ];
        return { brandId: bid, brandName: nameOf(bid), lines, option: still.length ? undefined : 'Also remove it from the campaign' };
      }));
    } catch {
      doSubmit([]);
    }
  };
  const doSubmit = async (alsoFromCampaign: string[]) => {
    setRemovalItems(null);
    const cleanedProofUrl = normalizeUrl(proofUrl);
    try {
      let saved: CampaignAsset;
      if (editing) {
        saved = await updateAsset.mutateAsync({
          id: asset!.id,
          patch: {
            title: title.trim(),
            category,
            asset_type: assetType,
            campaign: campaign || null,
            brands: selectedBrands,
            dedicated_brand: category === 'Email' ? (dedicatedBrand || null) : null,
            proof_url: cleanedProofUrl,
            proof_screenshot: proofShots[0] ?? '',
            proof_extra_screenshots: proofShots.slice(1),
            metrics: Object.fromEntries(
              Object.entries(metrics)
                .filter(([, v]) => String(v).trim() !== '')
                .map(([k, v]) => [k, Number(v)]),
            ),
            publish_date: publishDate || null,
            end_date: endDate || null,
            publish_time: publishTime || null,
            brand_spots: spotsValue,
            sold_as: soldAsValue,
            status,
            description,
            caption: category === 'Social Media' ? caption : '',
          },
        });
        if (cleanedProofUrl !== proofUrl) setProofUrl(cleanedProofUrl);
        if (alsoFromCampaign.length && campaign) {
          const camp = await getCampaign(campaign);
          await updateCampaign.mutateAsync({
            id: campaign,
            patch: { brands: (camp.brands ?? []).filter((b) => !alsoFromCampaign.includes(b)) },
          });
        }
        toast.push('Placement updated');
      } else {
        saved = await createAsset.mutateAsync({
          title: title.trim(),
          category,
          asset_type: assetType,
          campaign: campaign || null,
          brands: selectedBrands,
          dedicated_brand: category === 'Email' ? (dedicatedBrand || null) : null,
          publish_date: publishDate || null,
          end_date: endDate || null,
          publish_time: publishTime || null,
          brand_spots: spotsValue,
          sold_as: soldAsValue,
          status,
          description,
          caption: category === 'Social Media' ? caption : '',
        });
        toast.push('Placement added');
      }

      if (createTaskAlso && taskName.trim() && taskDueDate) {
        const slug = slugify(taskName.slice(0, 40));
        const taskId = `t-${slug}-${Math.random().toString(36).slice(2, 6)}`;
        await createTask.mutateAsync({
          id: taskId,
          name: taskName.trim(),
          due_date: taskDueDate,
          status: 'Not started',
          campaign: campaign || null,
          asset: saved.id,
          assignees: taskAssigneeIds,
        });
      }
      onCreated?.(saved);
      onClose();
    } catch (err) {
      console.error(err);
      const reason = firstErrorMessage(
        (err as { response?: { data?: unknown } } | null)?.response?.data,
      );
      const base = editing ? 'Could not update placement' : 'Could not create placement';
      toast.push(reason ? `${base} — ${reason}` : base);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit placement' : duplicateOf ? 'Duplicate placement' : 'New placement'}
      maxWidth={620}
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          {editing && (
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
              style={{ color: 'var(--warn)' }}
            >
              Delete asset
            </Button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={!canSubmit}>
              {editing ? 'Save changes' : 'Add placement'}
            </Button>
          </div>
        </div>
      }
    >
      {editing && asset?.campaign && asset.campaign_name && (
        <a
          href={`/campaigns/${asset.campaign}`}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            marginBottom: 12,
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border)',
            borderRadius: 999,
            fontSize: 12,
            color: 'var(--ink-2)',
            textDecoration: 'none',
          }}
          title="Open campaign in a new tab"
        >
          ↗ Part of campaign: <strong style={{ fontWeight: 600 }}>{asset.campaign_name}</strong>
        </a>
      )}

      <Field label="Label (optional)">
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Extra note shown after the name — e.g. announcement, teaser shot"
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Category" required>
          <select
            className="input"
            value={category}
            onChange={(e) => setCategory(e.target.value as AssetCategory)}
          >
            {ASSET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Type" required>
          <select
            className="input"
            value={assetType}
            onChange={(e) => setAssetType(e.target.value as AssetType)}
          >
            {allowedTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>

      {/* Brand spots sits right under Category/Type, in its own tinted box,
          so it's visible without scrolling past the long brand list. */}
      {(() => {
        const taken = selectedBrands.length;
        const over = !!spotsValue && taken > spotsValue;
        const full = !!spotsValue && taken === spotsValue;
        return (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
              padding: '10px 12px', marginBottom: 14,
              border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
              background: spotsValue ? '#f3faf5' : 'var(--bg-subtle)',
            }}
          >
            <Icon name="user" size={16} />
            <div style={{ flex: '1 1 180px', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Brand slots</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                {spotsValue
                  ? 'Tick a brand under Brands to give it a slot.'
                  : 'For placements brands can book (e.g. a product grid = 6). Leave empty for a normal placement.'}
              </div>
            </div>
            <input
              className="input"
              type="number"
              min={1}
              max={99}
              placeholder="—"
              aria-label="Number of brand slots"
              value={brandSpots}
              onChange={(e) => setBrandSpots(e.target.value)}
              style={{ width: 72 }}
            />
            {spotsValue ? (
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 10px', borderRadius: 'var(--r-pill)', fontSize: 12, fontWeight: 600,
                  background: over ? 'var(--warn-bg)' : full ? 'var(--bg-subtle)' : '#e6f4ea',
                  color: over ? 'var(--warn)' : full ? 'var(--ink-4)' : '#1e7a3c',
                }}
              >
                <Icon name="user" size={11} />
                {taken} / {spotsValue} slot{spotsValue === 1 ? '' : 's'} taken
                {over ? ' — too many brands!' : ''}
              </span>
            ) : null}
            {spotsValue ? (
              <div style={{ flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid var(--border-soft)' }}>
                <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink-3)', minWidth: 120 }}>Sold as</span>
                {soldAsChoices.length === 0 ? (
                  <span style={{ fontSize: 11.5, color: 'var(--warn)' }}>
                    Nothing on Placements &amp; prices sells this type yet — tick it as Sellable there.
                  </span>
                ) : soldAsChoices.length === 1 ? (
                  <span
                    title="Only one way to sell this placement — set automatically."
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', borderRadius: 'var(--r-md)',
                      background: 'var(--bg-subtle)', border: '1px solid var(--border)',
                      fontSize: 13, color: 'var(--ink-4)', cursor: 'not-allowed',
                    }}
                  >
                    {soldAsChoices[0].label}
                    <span style={{ fontSize: 11 }}>· automatic</span>
                  </span>
                ) : (
                  <select
                    className="input"
                    value={soldAsValue != null ? String(soldAsValue) : ''}
                    onChange={(e) => setSoldAs(e.target.value)}
                    style={{ width: 'auto', minWidth: 220 }}
                    aria-label="Sold as"
                  >
                    <option value="">— pick what brands book —</option>
                    {soldAsChoices.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label}{(r.pack_types ?? []).length ? ` (with ${r.pack_types.map((t) => t.replace('Instagram ', '').toLowerCase()).join(' + ')})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            ) : null}
          </div>
        );
      })()}

      <Field
        label="Campaign"
        hint={!campaign && selectedBrands.length === 0
          ? 'Pick a campaign, or tag one or more brands below.'
          : !campaign
          ? 'Standalone — surfaces under the brand(s) selected below.'
          : undefined}
      >
        <select
          className="input"
          value={campaign}
          onChange={(e) => setCampaign(e.target.value)}
        >
          <option value="">No campaign (standalone)</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>

      <Field
        label="Brands"
        hint={
          campaign
            ? 'Optional — extra brands to surface this placement under, beyond the campaign.'
            : 'Required when no campaign is selected.'
        }
      >
        {spotsValue && campaign ? (
          <div style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 'var(--r-md)', background: 'var(--bg-subtle)', fontSize: 12, lineHeight: 1.45, color: 'var(--ink-3)' }}>
            Brands normally get here by booking on the booking page. A brand you tick here is a free fill-in: no booking,
            no invoice. For a paying brand, send its booking link instead.
          </div>
        ) : null}
        <BrandChipPicker
          brands={brands}
          selected={selectedBrands}
          onToggle={toggleBrand}
          alphabetical
        />
        {/* A hero is tagged only with the brand(s) visible in it (Doreen,
            2026-10-29) — several brands is possible but rarely right. */}
        {assetType.startsWith('Hero Banner') && selectedBrands.length > 1 && (
          <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 'var(--r-md)', background: '#fff7e0', color: '#7a5300', fontSize: 12, lineHeight: 1.45 }}>
            {selectedBrands.length} brands on one hero. Only tag the brand(s) whose products are actually visible in the banner.
          </div>
        )}
      </Field>


      {category === 'Email' && selectedBrands.length > 0 && (
        <Field
          label="Dedicated to"
          hint="Counts this newsletter as 'Dedicated' on that brand's visibility plan."
        >
          <select
            className="input"
            value={dedicatedBrand}
            onChange={(e) => setDedicatedBrand(e.target.value)}
          >
            <option value="">Not dedicated (multi-brand feature)</option>
            {brands
              .filter((b) => selectedBrands.includes(b.id))
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
          </select>
        </Field>
      )}

      {capacityConflicts.length > 0 && (
        <div
          style={{
            padding: '9px 12px', borderRadius: 8, fontSize: 12.5,
            background: capacityConflicts.some((c) => c.severity === 'hard') ? 'var(--warn-bg)' : '#fff7e0',
            color: capacityConflicts.some((c) => c.severity === 'hard') ? 'var(--warn)' : '#7a5300',
            display: 'flex', flexDirection: 'column', gap: 3,
          }}
        >
          <strong>
            {capacityConflicts.some((c) => c.severity === 'hard')
              ? 'Heads up — this clashes with the homepage schedule:'
              : 'Possible, but not ideal — already planned in this period:'}
          </strong>
          {capacityConflicts.slice(0, 4).map((c) => (
            <span key={c.asset.id}>• {conflictLine(c)}</span>
          ))}
          {capacityConflicts.length > 4 && <span>…and {capacityConflicts.length - 4} more</span>}
          <span style={{ opacity: 0.8 }}>You can still save — just make sure it's intentional.</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field
          label="Publish date"
          hint={publishDate ? undefined : 'Leave empty to add to the "To plan" tray.'}
        >
          <input
            className="input"
            type="date"
            value={publishDate}
            onChange={(e) => setPublishDate(e.target.value)}
          />
          {(category === 'Email' || category === 'Social Media') && (
            <div style={{ marginTop: 6 }}>
              <HeatmapDatePicker
                value={publishDate}
                onPick={(iso) => setPublishDate(iso)}
                category={category}
                excludeAssetId={asset?.id}
              />
            </div>
          )}
        </Field>
        <Field label="End date" hint="Optional — for placements that run over a range.">
          <input
            className="input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Field>
        <Field label="Time">
          <input
            className="input"
            type="time"
            value={publishTime}
            onChange={(e) => setPublishTime(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Status">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ASSET_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip ${statusClass(s)}`}
              onClick={() => setStatus(s)}
              style={{
                cursor: 'pointer',
                border: status === s ? '1.5px solid currentColor' : '1.5px solid transparent',
                padding: '5px 10px',
                fontSize: 12.5,
                fontWeight: status === s ? 700 : 500,
                opacity: status === s ? 1 : 0.7,
              }}
            >
              <span className="dot" style={{ background: 'currentColor' }} />
              {s}
            </button>
          ))}
        </div>
      </Field>

      {category === 'Social Media' && (
        <Field label="Caption">
          <textarea
            className="input"
            rows={3}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="What does the post say?"
          />
        </Field>
      )}

      {assetType === 'Banner on PDP' && (
        <div
          style={{
            marginTop: 4,
            marginBottom: 12,
            padding: '12px 14px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            background: 'var(--bg-elevated)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>Shop banner</div>
            {editing && asset ? (
              <>
                <ShopBannerStatus
                  state={asset.shop_banner?.live_state ?? 'Draft'}
                  inSync={asset.shop_banner?.in_sync ?? null}
                  notSetUp={!asset.shop_banner?.exists}
                />
                {asset.shop_banner?.headline && (
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {asset.shop_banner.headline.split('\n')[0]}
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
                Add the placement first, then set up the banner text, products and dates for the shop.
              </div>
            )}
          </div>
          {editing && asset && (
            <Button onClick={() => { onClose(); navigate(`/shop-banners/${asset.id}`); }}>
              {asset.shop_banner?.exists ? 'Edit shop banner' : 'Set up shop banner'}
            </Button>
          )}
        </div>
      )}

      <Field label="Description">
        <textarea
          className="input"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional context for the team."
        />
      </Field>

      <div
        style={{
          marginTop: 12,
          padding: 12,
          background: 'var(--bg-subtle)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={createTaskAlso}
            onChange={(e) => {
              setCreateTaskAlso(e.target.checked);
              if (e.target.checked && !taskDueDate) setTaskDueDate(publishDate);
              if (e.target.checked && !taskName) {
                const t = title.trim();
                setTaskName(`Prepare: ${shortAssetType(assetType)}${t ? ` · ${t}` : ''}`);
              }
            }}
          />
          {editing ? 'Add a task for this placement' : 'Also create a task for this placement'}
        </label>
        {createTaskAlso && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 10 }}>
              <Field label="Task name">
                <input
                  className="input"
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  placeholder="e.g. Write caption + brief photographer"
                />
              </Field>
              <Field label="Due date">
                <input
                  className="input"
                  type="date"
                  value={taskDueDate}
                  onChange={(e) => setTaskDueDate(e.target.value)}
                />
              </Field>
            </div>
            <Field label="Assignees">
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: 6,
                    border: '1px dashed var(--border-strong)',
                    borderRadius: 6,
                    minHeight: 36,
                    cursor: 'pointer',
                  }}
                  onClick={() => setTaskPickerOpen((v) => !v)}
                >
                  {taskAssigneeIds.length === 0 ? (
                    <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>
                      Click to assign teammates
                    </span>
                  ) : (
                    <>
                      <AssigneeStack
                        users={team.filter((u) => taskAssigneeIds.includes(u.user_id))}
                        size={22}
                      />
                      <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                        {taskAssigneeIds.length} assigned
                      </span>
                    </>
                  )}
                </div>
                {taskPickerOpen && (
                  <AssigneePicker
                    value={taskAssigneeIds}
                    users={team}
                    onChange={setTaskAssigneeIds}
                    onClose={() => setTaskPickerOpen(false)}
                  />
                )}
              </div>
            </Field>
          </>
        )}
      </div>

      {editing && asset && <CommentsSection assetId={asset.id} />}

      {editing && (
        <div
          // Cmd+V anywhere in the box with a screenshot on the clipboard
          // adds it to the proof (text pastes into the link field as usual).
          onPaste={(e) => {
            const file = Array.from(e.clipboardData.files).find((f) => f.type.startsWith('image/'));
            if (!file) return;
            e.preventDefault();
            void takeScreenshot(file);
          }}
          style={{
            marginTop: 12,
            padding: '12px 14px',
            background: 'var(--bg-subtle)',
            borderRadius: 'var(--r-md)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--ink-4)',
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            Proof of delivery
          </div>
          <Field label="Live URL" hint="e.g. an Instagram permalink or the page where this banner appears.">
            <input
              className="input"
              type="url"
              value={proofUrl}
              onChange={(e) => setProofUrl(e.target.value)}
              placeholder="https://"
            />
          </Field>
          <Field label="Screenshot">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={screenshotLoading}
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  void (async () => {
                    for (const file of files) await takeScreenshot(file);
                  })();
                }}
                style={{ fontSize: 12 }}
              />
              <button
                type="button"
                className="link"
                disabled={screenshotLoading}
                title="Use the screenshot on your clipboard (Cmd+Ctrl+Shift+4 on a Mac) — or press Cmd+V here"
                onClick={async () => {
                  try {
                    await takeScreenshot(await pastedPicture());
                  } catch (err) {
                    toast.push(err instanceof Error ? err.message : 'Could not paste.');
                  }
                }}
                style={{ fontSize: 12 }}
              >
                or paste from clipboard
              </button>
              {screenshotLoading && (
                <span style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                  Reading…
                </span>
              )}
            </div>
            {proofShots.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {proofShots.map((shot, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={() => openDataUrl(shot)}
                      style={{
                        display: 'block',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--r-sm)',
                        padding: 4,
                        background: 'var(--bg-elevated)',
                        cursor: 'zoom-in',
                      }}
                      title="Open full size"
                    >
                      <img
                        src={shot}
                        alt={`Proof screenshot ${i + 1}`}
                        style={{ display: 'block', maxWidth: 160, maxHeight: 110, objectFit: 'contain' }}
                      />
                    </button>
                    {isAnimatedPicture(shot) && (
                      <span
                        style={{
                          position: 'absolute', left: 8, bottom: 8, padding: '1px 6px', borderRadius: 999,
                          background: 'rgba(20, 30, 25, 0.72)', color: '#fff', fontSize: 10.5, pointerEvents: 'none',
                        }}
                      >
                        ▶ moving
                      </span>
                    )}
                    <button
                      type="button"
                      aria-label={`Remove screenshot ${i + 1}`}
                      title="Remove"
                      onClick={() => setProofShots((prev) => prev.filter((_, j) => j !== i))}
                      style={{
                        position: 'absolute', top: -7, right: -7, width: 20, height: 20, borderRadius: '50%',
                        border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--ink-3)',
                        fontSize: 12, lineHeight: '18px', padding: 0, cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>
        </div>
      )}
      {editing && (METRIC_FIELDS_BY_CATEGORY[category]?.length ?? 0) > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: '12px 14px',
            background: 'var(--bg-subtle)',
            borderRadius: 'var(--r-md)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--ink-4)',
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            Results
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginBottom: 10 }}>
            Fill these in after publishing — they show up in the lookbook.
          </div>
          {category === 'Social Media' && asset && (
            <InstagramLink
              key={asset.id}
              asset={asset}
              onLinked={(saved) => {
                setMetrics(
                  Object.fromEntries(
                    Object.entries(saved.metrics ?? {}).map(([k, v]) => [k, v == null ? '' : String(v)]),
                  ),
                );
                setProofShots([saved.proof_screenshot, ...(saved.proof_extra_screenshots ?? [])].filter(Boolean));
                setProofUrl(saved.proof_url ?? '');
              }}
            />
          )}
          {category === 'Email' && asset && (
            <KlaviyoLink
              key={asset.id}
              asset={asset}
              onLinked={(saved) => setMetrics(
                Object.fromEntries(
                  Object.entries(saved.metrics ?? {}).map(([k, v]) => [k, v == null ? '' : String(v)]),
                ),
              )}
            />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {(METRIC_FIELDS_BY_CATEGORY[category] ?? []).map((f) => (
              <Field key={f.key} label={f.label}>
                <input
                  className="input"
                  type="number"
                  min={0}
                  value={metrics[f.key] ?? ''}
                  onChange={(e) => setMetrics((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder="—"
                />
              </Field>
            ))}
          </div>
          {computeRates(
            category,
            Object.fromEntries(
              Object.entries(metrics)
                .filter(([, v]) => String(v).trim() !== '')
                .map(([k, v]) => [k, Number(v)]),
            ),
          ).length > 0 && (
            <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 12 }}>
              {computeRates(
                category,
                Object.fromEntries(
                  Object.entries(metrics)
                    .filter(([, v]) => String(v).trim() !== '')
                    .map(([k, v]) => [k, Number(v)]),
                ),
              ).map((r) => (
                <span key={r.label} style={{ color: 'var(--ink-3)' }}>
                  {r.label}: <strong style={{ color: 'var(--ink)' }}>{r.value}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {editing && asset && (
        <MeasuredOnSite
          data={measured.data}
          loading={measured.isLoading}
          onCopy={async (id) => {
            try {
              await navigator.clipboard.writeText(id);
              toast.push('Placement ID copied');
            } catch {
              toast.push('Could not copy — the ID is ' + id);
            }
          }}
        />
      )}
      <BrandRemovalCheck
        open={!!removalItems}
        items={removalItems ?? []}
        busy={updateAsset.isPending}
        onConfirm={(also) => doSubmit(also)}
        onClose={() => setRemovalItems(null)}
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={onDelete}
        danger
        title={`Delete ${asset?.title || 'this placement'}?`}
        description="This permanently removes the placement and unlinks any tasks tied to it. Cannot be undone."
        confirmLabel="Delete placement"
        busy={deleteAsset.isPending}
      />
    </Modal>
  );
};

export default CreateAssetModal;

const fmtInt = (n: number) => n.toLocaleString('en-GB');

/**
 * Read-only block with the numbers the storefront itself counted for this
 * placement (via perfy). Shown next to the hand-typed Results so the two are
 * never confused: measured = what the site saw, Results = what Doreen entered
 * from Klaviyo / Meta.
 */
function MeasuredOnSite({
  data,
  loading,
  onCopy,
}: {
  data: import('../../lib/api/assets').PlacementMetrics | undefined;
  loading: boolean;
  onCopy: (id: string) => void;
}) {
  const label = (
    <div
      style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'var(--ink-4)',
        fontWeight: 600,
        marginBottom: 8,
      }}
    >
      Measured on site
    </div>
  );
  const wrap = (children: React.ReactNode) => (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        background: 'var(--bg-subtle)',
        borderRadius: 'var(--r-md)',
      }}
    >
      {label}
      {children}
    </div>
  );
  if (loading || !data) return wrap(<div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>);

  const idRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginBottom: 8 }}>
      <span style={{ color: 'var(--ink-3)' }}>Placement ID for the Shopify theme:</span>
      <code className="mono" style={{ fontSize: 12, padding: '1px 6px', background: 'var(--bg-elevated)', borderRadius: 4 }}>
        {data.placement_id}
      </code>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onCopy(data.placement_id)}>
        Copy
      </button>
    </div>
  );

  if (!data.configured) {
    return wrap(
      <>
        {idRow}
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
          mosy is not connected to perfy yet, so no site measurements can be shown.
        </div>
      </>,
    );
  }
  if (data.error) {
    return wrap(
      <>
        {idRow}
        <div style={{ fontSize: 12, color: 'var(--warn)' }}>
          Could not reach perfy right now ({data.error}). The numbers will be back when it is.
        </div>
      </>,
    );
  }
  const totals = data.weekly.reduce(
    (acc, w) => ({ views: acc.views + w.views, clicks: acc.clicks + w.clicks }),
    { views: 0, clicks: 0 },
  );
  const reachByWeek = new Map(data.page_reach_weekly.map((r) => [r.week, r]));
  return wrap(
    <>
      {idRow}
      {data.weekly.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
          No views recorded yet. Paste the Placement ID into the matching section in the Shopify
          theme editor; numbers appear the day after the first visitors see it.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 14, fontSize: 12, marginBottom: 8 }}>
            <span style={{ color: 'var(--ink-3)' }}>
              Views: <strong style={{ color: 'var(--ink)' }}>{fmtInt(totals.views)}</strong>
            </span>
            <span style={{ color: 'var(--ink-3)' }}>
              Clicks: <strong style={{ color: 'var(--ink)' }}>{fmtInt(totals.clicks)}</strong>
            </span>
            <span style={{ color: 'var(--ink-3)' }}>
              CTR:{' '}
              <strong style={{ color: 'var(--ink)' }}>
                {totals.views ? `${((totals.clicks / totals.views) * 100).toFixed(1)}%` : '—'}
              </strong>
            </span>
          </div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--ink-4)', textAlign: 'left' }}>
                <th style={{ fontWeight: 500, paddingBottom: 4 }}>Week</th>
                <th style={{ fontWeight: 500, textAlign: 'right' }}>Views</th>
                <th style={{ fontWeight: 500, textAlign: 'right' }}>Clicks</th>
                <th style={{ fontWeight: 500, textAlign: 'right' }}>CTR</th>
                {data.page_reach_weekly.length > 0 && (
                  <th style={{ fontWeight: 500, textAlign: 'right' }}>Page reach (GA4)</th>
                )}
              </tr>
            </thead>
            <tbody>
              {data.weekly.map((w) => (
                <tr key={`${w.week}-${w.placement_type}-${w.placement_brand}`}>
                  <td className="mono" style={{ padding: '3px 0' }}>{w.week_start}</td>
                  <td className="mono tnum" style={{ textAlign: 'right' }}>{fmtInt(w.views)}</td>
                  <td className="mono tnum" style={{ textAlign: 'right' }}>{fmtInt(w.clicks)}</td>
                  <td className="mono tnum" style={{ textAlign: 'right' }}>
                    {w.ctr == null ? '—' : `${(w.ctr * 100).toFixed(1)}%`}
                  </td>
                  {data.page_reach_weekly.length > 0 && (
                    <td className="mono tnum" style={{ textAlign: 'right', color: 'var(--ink-4)' }}>
                      {reachByWeek.get(w.week) ? `${fmtInt(reachByWeek.get(w.week)!.sessions)} sessions` : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <div style={{ fontSize: 11, color: 'var(--ink-5)', marginTop: 8 }}>
        Views and clicks are counted by lamptwist.com itself, for every visitor. Page reach comes
        from Google Analytics and undercounts visitors who refused cookies.
        {data.freshness?.placements_through && ` Data through ${data.freshness.placements_through}.`}
      </div>
    </>,
  );
}
